import { Box, IconButton, Tooltip, type TooltipProps } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { type ReactElement, type ReactNode, useId, useRef } from 'react';
import { useInToolRail } from '~/components/toolbar/shared/ToolRailSlots';
import { useToolTrayOpen } from '~/components/toolbar/shared/ToolTrayPresenceProvider';

interface ToolbarButtonProps {
    icon: ReactElement;
    /**
     * Shown on hover and keyboard focus, except while a tool tray is open in
     * the enclosing {@link ToolTrayPresenceProvider} scope: the tray hangs
     * under the buttons, where the tooltip would cover it. Hiding it leaves
     * the button's `aria-label` as it is.
     */
    tooltip: ReactNode;
    /**
     * Which way the tooltip opens. Rail buttons sit closer together than the
     * tooltip's own offset, so a sideways tooltip lands on the neighbour. The
     * step rail sits in the app header at the top of the window, so below is
     * its only free side. In a tool rail the tooltip opens to the left,
     * away from the tool options dock, whatever this says.
     */
    tooltipPlacement?: TooltipProps['placement'];
    selected?: boolean;
    /**
     * A highlight styled like {@link selected} that does not say the tool is
     * active, and carries no ARIA pressed state: the button's action just
     * ran from a keyboard shortcut, or its tool waits to arm. A lasting one
     * says why in {@link description}.
     */
    flashing?: boolean;
    disabled?: boolean;
    /**
     * Names the button for a tool options dock that lines up with it (the
     * dock's `anchor`), such as the dock of the operation it opens.
     */
    dockAnchor?: string;
    onClick?: () => void;
    anchorRef?: React.MutableRefObject<HTMLElement | null>;
    /**
     * Accessible name, for callers whose tooltip is a sentence rather than
     * the control's name (or is a node). Without it such a button announces
     * its whole tooltip, or — for a node tooltip — nothing at all. Defaults
     * to the tooltip when that is already a plain string.
     */
    ariaLabel?: string;
    ariaPressed?: boolean;
    /** Read out after the button's name, such as why it is highlighted. */
    description?: string;
    'data-testid'?: string;
}

export default function ToolbarButton({
    icon,
    tooltip,
    tooltipPlacement = 'bottom',
    selected = false,
    flashing = false,
    disabled = false,
    dockAnchor,
    onClick,
    anchorRef,
    ariaLabel,
    ariaPressed,
    description,
    'data-testid': testId,
}: ToolbarButtonProps) {
    const internalRef = useRef<HTMLButtonElement | null>(null);
    const descriptionId = useId();
    const trayOpen = useToolTrayOpen();
    const inToolRail = useInToolRail();

    const setRef = (el: HTMLButtonElement | null) => {
        internalRef.current = el;
        if (anchorRef) {
            anchorRef.current = el;
        }
    };

    const highlighted = selected || flashing;

    return (
        <Tooltip
            // An empty title keeps the tooltip shut, and closes one already
            // showing when a tray opens under the pointer.
            title={trayOpen ? '' : tooltip}
            placement={inToolRail ? 'left' : tooltipPlacement}
        >
            <span>
                <IconButton
                    ref={setRef}
                    size="small"
                    disabled={disabled}
                    onClick={onClick}
                    data-testid={testId}
                    aria-label={
                        ariaLabel ??
                        (typeof tooltip === 'string' ? tooltip : undefined)
                    }
                    aria-pressed={ariaPressed}
                    aria-describedby={description ? descriptionId : undefined}
                    data-flashing={flashing ? 'true' : undefined}
                    data-highlighted={highlighted ? 'true' : undefined}
                    data-dock-anchor={dockAnchor}
                    sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        color: highlighted
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        backgroundColor: highlighted
                            ? 'rgba(25, 118, 210, 0.12)'
                            : 'transparent',
                        border: highlighted
                            ? `1px solid ${theme.palette.primary.main}`
                            : '1px solid transparent',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                        },
                        '& svg': {
                            width: '100%',
                            height: '100%',
                            fontSize: 'inherit',
                        },
                    })}
                >
                    {icon}
                </IconButton>
                {description && (
                    <Box
                        component="span"
                        id={descriptionId}
                        sx={visuallyHidden}
                    >
                        {description}
                    </Box>
                )}
            </span>
        </Tooltip>
    );
}
